import { z } from 'zod';
import { DEFAULT_ACCENT } from '../lib/accentColor';
import { FONT_CHOICE_KEYS } from '../lib/fontFamily';
import { OCTO_SOUNDS } from '@/shared/lib/sound/octoSounds';

export const SUPPORTED_LANGUAGES = ['tr-TR', 'en-US', 'de-DE', 'fr-FR', 'it-IT', 'ru-RU'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const preferencesSchema = z.object({
  v: z.literal(1).default(1),
  menuPosition: z.enum(['sidebar', 'top', 'rail']).default('sidebar'),
  theme: z.enum(['light', 'dark', 'system']).default('system'),
  fontScale: z.union([z.literal(0.875), z.literal(1), z.literal(1.125), z.literal(1.25)]).default(1),
  density: z.enum(['compact', 'comfortable']).default('comfortable'),
  /**
   * Yüksek kontrast: çizgiler ve ikincil metinler koyulaşır (bkz. theme.css [data-contrast]).
   * Sahadaki eski/soluk monitörlerde ince gri çerçeveler ve etiketler görünmüyordu.
   */
  highContrast: z.boolean().default(false),
  /** Vurgu rengi (#rrggbb). Marka ton merdiveni bundan türetilir. */
  accentColor: z
    .string()
    .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
    .default(DEFAULT_ACCENT),
  /** Yazı tipi ailesi — sistemde bulunan seçeneklerden biri. */
  fontFamily: z.enum(FONT_CHOICE_KEYS as [string, ...string[]]).default('default'),
  language: z.enum(SUPPORTED_LANGUAGES).default('tr-TR'),
  homeLayout: z.enum(['widgets', 'simple']).default('widgets'),
  sidebarCollapsed: z.boolean().default(false),
  /** Üst menüde modüller adsız, yalnız simge (sol menünün daraltılmışı gibi). */
  topNavCompact: z.boolean().default(false),
  tabsRestore: z.boolean().default(true),
  startPage: z
    .union([z.literal('home'), z.literal('lastTab'), z.object({ screenId: z.string() })])
    .default('home'),
  /** Octo bildirim sesleri (shared/lib/sound/octoSounds): mesaj ve bildirim için ayrı seçim. */
  soundMessage: z.enum(OCTO_SOUNDS).default('octo'),
  soundNotification: z.enum(OCTO_SOUNDS).default('chime'),
  soundVolume: z.number().min(0).max(1).default(0.6),
  /** Uygulama açıkken gelen mesaj/bildirim için masaüstü (sistem) bildirimi göster. */
  desktopNotifications: z.boolean().default(true),
});

export type Preferences = z.infer<typeof preferencesSchema>;
export const PREFS_SETTING_KEY = 'web.preferences';
export const PREFS_CACHE_KEY = 'octo.web.prefs.cache';

/** Bilinmeyen/bozuk değerlerde varsayılanlara düşer (ileri uyumluluk). */
export function parsePreferences(raw: unknown): Preferences {
  const res = preferencesSchema.safeParse(raw ?? {});
  if (res.success) return res.data;
  // alan bazında kurtarma
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const clean: Record<string, unknown> = {};
  for (const key of Object.keys(preferencesSchema.shape) as (keyof Preferences)[]) {
    const single = preferencesSchema.shape[key].safeParse(obj[key]);
    if (single.success) clean[key] = single.data;
  }
  return preferencesSchema.parse(clean);
}
